import { components } from '@feedfm/clips-services-api';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { ClipList } from '../../../components/ClipList';
import { CollectionCard } from '../../../components/CollectionCard';
import styles from './Search.styles';
import { FallbackSuggestion } from './useSearchWithFallback';

interface SearchResultsProps {
  searchQuery: string;
  searchResults: components['schemas']['SearchResponse'];
  fallbackSuggestion: FallbackSuggestion | null;
  fallbackClips: components['schemas']['Clip'][];
  fallbackLoading: boolean;
  semanticResults: components['schemas']['Clip'][];
  semanticSearchTerms: string[];
  semanticLoading: boolean;
}

export const SearchResults = ({
  searchQuery,
  searchResults,
  fallbackSuggestion,
  fallbackClips,
  fallbackLoading,
  semanticResults,
  semanticSearchTerms,
  semanticLoading,
}: SearchResultsProps) => {
  return (
    <Box sx={styles.resultsSection}>
      {/* Search results header */}
      <Box sx={styles.resultsHeader}>
        <Typography variant="h5" sx={styles.resultsHeaderTitle}>
          Search Results
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={styles.resultsHeaderSubtitle}
        >
          Found {searchResults.totalResults} result
          {searchResults.totalResults !== 1 ? 's' : ''} for "{searchQuery}"
        </Typography>
      </Box>

      {/* Always show clips section first */}
      <Box sx={styles.sectionContainer}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          Clips{' '}
          {searchResults.clipsCount &&
            `(Showing ${searchResults.clips.length} of ${searchResults.clipsCount})`}
        </Typography>
        {searchResults.clips.length > 0 ? (
          <ClipList
            clips={searchResults.clips}
            variant="cards"
            showIds={true}
          />
        ) : (
          <Box sx={styles.noClipsContainer}>
            <Typography variant="body2" color="text.secondary">
              No clips found matching your search.
            </Typography>

            {/* Fallback: show similar clips from audio catalog */}
            {(fallbackLoading || fallbackClips.length > 0) && (
              <Box>
                {fallbackSuggestion && (
                  <Typography variant="subtitle2" sx={styles.fallbackHeader}>
                    {fallbackSuggestion.matchType === 'track_and_artist'
                      ? `Similar clips to "${fallbackSuggestion.trackTitle}" by ${fallbackSuggestion.artistName}`
                      : `Similar clips to ${fallbackSuggestion.artistName}`}
                  </Typography>
                )}
                {fallbackLoading ? (
                  <Box sx={styles.inlineLoading}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Finding similar clips…
                    </Typography>
                  </Box>
                ) : (
                  <ClipList clips={fallbackClips} variant="cards" />
                )}
              </Box>
            )}

            {/* Semantic search results */}
            {(semanticLoading || semanticResults.length > 0) && (
              <Box>
                <Typography variant="subtitle2" sx={styles.fallbackHeader}>
                  Semantic Search Results
                </Typography>
                {semanticSearchTerms.length > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={styles.semanticSearchTerms}
                  >
                    Matched on: {semanticSearchTerms.join(', ')}
                  </Typography>
                )}
                {semanticLoading ? (
                  <Box sx={styles.inlineLoading}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Searching across our full catalog…
                    </Typography>
                  </Box>
                ) : (
                  <ClipList clips={semanticResults} variant="cards" />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Always show collections section second */}
      <Box sx={styles.sectionContainer}>
        <Typography variant="h6" sx={styles.sectionTitle}>
          Collections{' '}
          {searchResults.collectionsCount &&
            `(Showing ${searchResults.collections.length} of ${searchResults.collectionsCount})`}
        </Typography>
        {searchResults.collections.length > 0 ? (
          <Grid container spacing={3} sx={styles.collectionsGrid}>
            {searchResults.collections.map((hit) => (
              <Grid
                size={{ xs: 12, sm: 6, md: 4 }}
                key={hit.collection.collectionId}
              >
                <CollectionCard
                  collection={hit.collection}
                  artworkPreSignedUrls={hit.artworkPreSignedUrls}
                  thumbnailUrl={hit.thumbnailUrl}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No collections found matching your search.
          </Typography>
        )}
      </Box>

      {/* Show no results message */}
      {searchResults.totalResults === 0 && (
        <Box sx={styles.noResultsContainer}>
          <Typography variant="body1" sx={styles.noResultsText}>
            No clips or collections found matching "{searchQuery}"
          </Typography>
        </Box>
      )}
    </Box>
  );
};
